import classNames from 'classnames';
import cloneDeepWith from 'lodash/cloneDeepWith';
import isNumber from 'lodash/isNumber';
import { LiveScoringSystem } from '@spikemark/live';
import { getLogger } from '@spikemark/logging';
import { ElasticCourseData, ElasticMatchplayMatchesMatchesInnerPlayersInner, RoundAnalyticsPlayerTypeEnum, TournamentEventTypeEnum, TournamentRequestFourpeopleTypesEnum, TournamentRequestTournamentPrivacyEnum } from '@spikemark/rest-api';
import { SummaryDatum } from '../components/design-system/summary-card';
import { TableRowData } from '../components/design-system/table';
import { ScoringIndicatorClassNames } from '../constants/scoring';
import { LeaderboardDisplayMode } from '../types/leaderboard';
import { HoleResult } from './types';
export const displayTime = (input: Date): string => {
  const newHours = input.getHours() > 12 ? input.getHours() - 12 : input.getHours();
  return [newHours, input.getMinutes()].map(x => String(x).padStart(2, '0')).join(':');
};
export function convertToAmericanDateFormat(dateString: string, timezone?: string): string {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: timezone || 'UTC'
  }).format(date);
  return formattedDate;
}
export const formatRawDate = (date: Date, includeYear?: boolean, timezone?: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: includeYear === false ? undefined : 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone || 'UTC'
  };
  const dateFormatter = new Intl.DateTimeFormat('en-US', options);
  return dateFormatter.format(date);
};
export const formatDate = (dateInput: string | Date, includeYear?: boolean, timezone?: string): string => {
  if (dateInput instanceof Date) {
    dateInput = dateInput.toISOString();
  }
  const date = new Date(dateInput);
  return formatRawDate(date, includeYear, timezone);
};
export const formatTournamentDate = ({
  startDate,
  endDate
}: {
  startDate: string | Date;
  endDate: string | Date;
}) => {
  const formattedEndDate = formatDate(endDate);
  if (String(startDate) === String(endDate)) {
    return formattedEndDate;
  }
  const formattedStartDate = formatDate(startDate, false);
  return `${formattedStartDate} - ${formattedEndDate}`;
};
export const rankToDisplayValue = (rank?: number) => {
  if (typeof rank === 'undefined' || Number.isNaN(rank)) {
    return '-';
  }
  if (rank > 9000 || rank === 0) {
    return 'N/A';
  } else {
    return `#${rank}`;
  }
};
export const strokesToDisplayValue = (strokes: number) => {
  if (Number.isNaN(strokes) || !strokes) {
    return '-';
  }
  if (strokes > 9000) {
    return 'N/A';
  } else {
    return strokes;
  }
};
export const scoreToDisplayValue = (score: number) => {
  if (score === 0) {
    return ' E';
  } else if (score > 10000) {
    return 'N/A';
  } else if (score > 0) {
    return `+${score}`;
  } else {
    return String(score);
  }
};
const holeResultToDisplayValue = (result: HoleResult, mode?: LeaderboardDisplayMode) => {
  const {
    par,
    strokes
  } = result;
  const score = (par - strokes) * -1;
  let displayValue = '';
  if (mode === 'score') {
    displayValue = scoreToDisplayValue(score);
  } else if (mode === 'stroke') {
    displayValue = String(strokesToDisplayValue(strokes));
  }
  return displayValue;
};
export const displayScoreResult = (result: HoleResult, mode?: LeaderboardDisplayMode) => {
  const {
    par,
    strokes
  } = result;
  const score = (par - strokes) * -1;
  const displayValue = holeResultToDisplayValue(result, mode);
  if (score > 0) {
    return <span className="text-bogey-blue font-semibold">{displayValue}</span>;
  } else if (score < 0) {
    return <span className="text-birdie-red font-semibold">{displayValue}</span>;
  } else {
    return <span className="font-semibold">{displayValue}</span>;
  }
};
export const displayHoleResult = (result: HoleResult, mode?: LeaderboardDisplayMode, monochrome?: boolean) => {
  const {
    par,
    strokes
  } = result;
  const score = (par - strokes) * -1;
  const displayValue = holeResultToDisplayValue(result, mode);
  let shapeClassParent = '';
  let shapeClassChild = '';
  if (score === -1) {
    shapeClassParent = ScoringIndicatorClassNames.BirdieOuterCircle;
  } else if (score === 1) {
    shapeClassParent = ScoringIndicatorClassNames.BogeyOuterSquare;
  } else if (score <= -2) {
    shapeClassParent = ScoringIndicatorClassNames.EagleOuterCircle;
    shapeClassChild = ScoringIndicatorClassNames.EagleInnerCircle;
  } else if (score >= 2) {
    shapeClassParent = ScoringIndicatorClassNames.BogeyOuterSquare;
    shapeClassChild = ScoringIndicatorClassNames.DoubleBogeyInnerSquare;
  }
  return score === 0 || strokes === 0 ? <span data-sentry-component="displayHoleResult" data-sentry-source-file="format.tsx">{displayValue}</span> : <div className={classNames('text-center inline-flex justify-center w-5 h-5 lg:w-6 lg:h-6 whitespace-nowrap p-1', {
    'bg-white': monochrome,
    'text-white': !monochrome && score > -2,
    'text-black': !monochrome && score <= -2
  }, shapeClassParent, !monochrome && {
    'bg-over-par-gray outline-gray-400': score >= 2,
    'bg-bogey-blue': score === 1,
    'bg-birdie-red': score === -1,
    'bg-eagle-yellow outline-eagle-yellow': score <= -2
  })} data-sentry-component="displayHoleResult" data-sentry-source-file="format.tsx">
      <div className={`self-center min-w-5 min-h-5 content-center ${shapeClassChild}`}>
        {displayValue}
      </div>
    </div>;
};
export const displayUsername = (name?: string, playertype?: RoundAnalyticsPlayerTypeEnum) => {
  if (playertype === RoundAnalyticsPlayerTypeEnum.Individual) {
    return `${name} (Ind)`;
  } else {
    return name;
  }
};

//We pass the identifier in here purely for debugging purposes
export const displayTeamName = (schoolName: string, teamLabel?: string, identifier?: string) => {
  if (!schoolName) {
    getLogger('client').warn(`Detected missing school name for: ${identifier}`);
    return '';
  }
  return `${schoolName.replace(/[\[\(][A-Z][\]\)]/, '')}${teamLabel && teamLabel !== 'A' ? ` (${teamLabel})` : ''}`;
};
export const displayCourseDetail = (course: ElasticCourseData): string => `${course.courseName} (${course.totalYards.toLocaleString()} yards) | Par ${course.totalPar}`;

//We're not recieving holes played in most places currently, as live scoring doesn't exist.
//However we will need to start mapping this properly as soon as we have the data
export const formatThru = (holesPlayed: number | string) => {
  if (holesPlayed == 0) {
    return '-';
  } else if (holesPlayed == 18) {
    return 'F';
  } else {
    return String(holesPlayed);
  }
};
export const formatRankingPoints = (points?: number) => {
  return isNumber(points) && points >= 0 ? points.toFixed(2) : '-';
};
export const formatFloatValue = (value?: number, fractionDigits?: number) => {
  return typeof value === 'number' ? value.toFixed(fractionDigits ?? 2) : '-';
};
export const formatStrengthOfField = (points?: number) => {
  return typeof points === 'number' ? points.toFixed(2) : '-';
};
export const formatPlayerScore = (result: Partial<Pick<ElasticMatchplayMatchesMatchesInnerPlayersInner, 'scoresDetail' | 'playerDetailUp' | 'holesLeft'>>, format: TournamentEventTypeEnum) => {
  if (result.playerDetailUp === 0) {
    return 'Tied';
  }
  if (!result.playerDetailUp) {
    return '-';
  }
  if (result.playerDetailUp > 1000) {
    return 'Win';
  }
  if (result.playerDetailUp < -1000) {
    return 'W/D';
  }
  const incompleteScores = result.scoresDetail?.some(x => x === null);
  const holesLeft = incompleteScores ? 18 - (result.scoresDetail?.filter(x => x !== null && x !== undefined).length ?? 0) : result.holesLeft ?? 0;
  const isMatchComplete = holesLeft > 0 && Math.abs(result.playerDetailUp) > holesLeft;
  if (holesLeft === 0 || !isMatchComplete || format === 'Medal Match Play') {
    if (result.playerDetailUp > 0) {
      return `${result.playerDetailUp}Up`;
    } else {
      return `${Math.abs(result.playerDetailUp)}Dn`;
    }
  } else if (holesLeft > 18) {
    return `${holesLeft} Holes`;
  } else {
    return `${Math.abs(result.playerDetailUp)}&${holesLeft}`;
  }
};
const shortDivisionLabels: Record<string, string> = {
  'NCAA Division I': 'DI',
  'NCAA Division II': 'DII',
  'NCAA Division III': 'DIII'
};
export const formatShortDivisionName = (division: string): string => {
  return shortDivisionLabels[division] ?? division;
};
export function isNonZero<T>(value: number | undefined, defaultValue: T): number | T {
  return value && value >= 0 ? value : defaultValue;
}

/**
 * Format a number as an ordinal, e.g. `3` to `3rd`
 * link https://stackoverflow.com/a/57518703/223225
 * @param {number} number To format
 */
const english_ordinal_rules = new Intl.PluralRules('en', {
  type: 'ordinal'
});
export function formatNumberOrdinal(number: number) {
  const category = english_ordinal_rules.select(number);
  switch (category) {
    case 'one':
      {
        return `${number}st`;
      }
    case 'two':
      {
        return `${number}nd`;
      }
    case 'few':
      {
        return `${number}rd`;
      }
    default:
      {
        return `${number}th`;
      }
  }
}
export function formatSignedNumber(number: number = 0): string {
  return number > 0 ? `+${number}` : `${number}`;
}
const scoringSystemValueMap: Record<LiveScoringSystem, string> = {
  scorecard_swap: 'Paperless Official Scoring',
  one_marks_all: 'Player Walking Scoring',
  volunteer_group: 'Volunteer Walking Scoring',
  volunteer_greenside: 'Greenside Scoring'
};
export function formatScoringSystem(val?: LiveScoringSystem) {
  return val ? scoringSystemValueMap[val] : '';
}
const fourpeopleTypesValueMap: Record<TournamentRequestFourpeopleTypesEnum, string> = {
  [TournamentRequestFourpeopleTypesEnum.Singles]: 'Singles',
  [TournamentRequestFourpeopleTypesEnum.Fourball]: 'Fourball',
  [TournamentRequestFourpeopleTypesEnum.Foursomes]: 'Foursomes'
};
export function formatMatchPlayFourpeopleType(fourpeopleType?: TournamentRequestFourpeopleTypesEnum) {
  return fourpeopleType ? fourpeopleTypesValueMap[fourpeopleType] : '';
}
export function formatTournamentPrivacy(privacy: TournamentRequestTournamentPrivacyEnum) {
  const privacyValueMap: Record<TournamentRequestTournamentPrivacyEnum, string> = {
    [TournamentRequestTournamentPrivacyEnum.Private]: 'Private',
    [TournamentRequestTournamentPrivacyEnum.Public]: 'Public',
    [TournamentRequestTournamentPrivacyEnum.ShareCode]: 'Secret Link'
  };
  return privacyValueMap[privacy];
}

/**
 * Ranger sorter funciton to be used in `.sort()`
 *
 * Compares two yard range strings for sorting
 * Inclusive of '6700-6899', '<' & '>' ranges
 * @param {string} a - First yard range string (e.g., '<5000', '6700-6899', '>7100')
 * @param {string} b - Second yard range string
 * @returns {number} Negative if a < b, positive if a > b, 0 if equal
 */
export function rangeSorter(a: string, b: string) {
  const getMinYard = (range: string) => {
    if (range.startsWith('<')) return 0;
    if (range.startsWith('>')) return parseInt(range.slice(1));
    return parseInt(range.split('-')[0]);
  };
  return getMinYard(a ?? '') - getMinYard(b ?? '');
}

/**
 * Date sorter funciton to be used in array `.sort()`
 *
 * Compares two dates for sorting
 * @param {Date} a - First date
 * @param {Date} b - Second date
 * @param {boolean} desc - Whether to sort in descending order
 * @returns {number} Negative if a < b, positive if a > b, 0 if equal
 */
export function dateSorter(a?: Date, b?: Date, desc?: boolean) {
  if (!a || !b) return 0;
  const compare = b.getTime() - a.getTime();
  return desc ? -compare : compare;
}
export function stripObjKeyPrefix<K extends string, V, P extends string>(input: Record<K, V>, prefix: P): Record<string, V> {
  return Object.entries(input).reduce((acc, [key, value]) => {
    const strippedKey = key.startsWith(prefix) ? key.slice(prefix.length) : key;
    acc[strippedKey] = value as V;
    return acc;
  }, {} as Record<string, V>);
}
export function addObjKeyPrefix<K extends string, V, P extends string>(input: Record<K, V>, prefix: P): Record<`${P}${K}`, V> {
  return Object.entries(input).reduce((acc, [key, value]) => {
    const newKey = `${prefix}${key}` as `${P}${K}`;
    acc[newKey] = value as V;
    return acc;
  }, {} as Record<`${P}${K}`, V>);
}
export type SummaryItem = {
  composeValue?: string;
  field: string;
  multiplier?: number;
  postfixTemplate?: string;
  precision?: number;
  sumFields?: Array<string>;
  title: SummaryDatum['title'];
  value?: TableRowData[string];
};
export function multiplyValue(value: number, multiplier: number | undefined, precision: number | undefined) {
  // multiply value - for use with percentages
  if (multiplier) {
    if (isNaN(value * multiplier)) {
      return value;
    } else {
      return parseFloat((value * multiplier).toFixed(precision));
    }
  }
  return value;
}
export function formatToSummary(data: TableRowData, summaryCardData: Array<SummaryItem>): SummaryDatum[] {
  return summaryCardData.map(d => {
    let value: TableRowData[string] = data?.[d.field] ?? '-';
    if (d.sumFields?.length) {
      value = d.sumFields.reduce<number>((acc, field) => acc + (data[field] as number ?? 0), 0);
    }
    if (value !== '-') {
      if (d.multiplier) {
        value = Number(value) * d.multiplier;
      }
      if (d.precision) {
        value = Number(formatStatsScore(Number(value), d.precision));
      }
    }
    if (d.composeValue) {
      value = composeString(d.composeValue, data);
    }
    let postfix: string | undefined;
    if (d.postfixTemplate) {
      postfix = composeString(d.postfixTemplate, data);
    }
    return {
      ...d,
      value,
      ...(postfix !== undefined ? {
        postfix
      } : {})
    };
  });
}
export function deepReplacePlaceholders<T>(input: T, replacements: Record<string, string>): T {
  return cloneDeepWith(input, value => {
    if (typeof value === 'string') {
      let replaced = value;
      for (const [key, replacement] of Object.entries(replacements)) {
        replaced = replaced.split(key).join(replacement);
      }
      return replaced;
    }
    return undefined; // let lodash handle recursion
  }) as T;
}

/**
 * Replaces [fieldName] placeholders in a format string with values from an object.
 * Supports optional modifiers, e.g. [value|percent:2]
 */
export function composeString(template: string, values: TableRowData): string {
  const res = template.replace(/\[([^\]]+)\]/g, (_, expr) => {
    const [rawKey, ...mods] = expr.split('|');
    const key = rawKey.trim();
    const rawVal = values[key] ?? '-';
    if (rawVal === '-') {
      return rawVal;
    }
    if (!mods.length) return String(rawVal);
    let num = Number(rawVal);
    let formatted: string | number = rawVal;
    for (const mod of mods) {
      const [name, arg] = mod.split(':');
      if (name === 'percent' && Number.isFinite(num)) {
        const n = Number(arg);
        const val = num * 100;
        formatted = formatStatsScore(val, n, 0).toString();
        num = val;
      }
    }
    return typeof formatted === 'string' ? formatted : Number.isFinite(num) ? String(num) : '';
  });
  return res === '---' ? '-' : res;
}
export const formatStatsScore = (value: string | number, precision: number, defaultPrecision?: number, trimEnd?: boolean) => {
  let digits: number | undefined = Number.isFinite(precision) ? precision : Number.isFinite(defaultPrecision) ? defaultPrecision : undefined;
  if (!Number.isFinite(value) || digits === undefined) {
    return (value || '').toString();
  }
  if (digits < 0) {
    digits = 0;
  } else if (digits > 100) {
    digits = 100;
  }
  if (Number(value) === 0) {
    return 0;
  }
  const fixed = Number(value).toFixed(digits);
  // remove .00 if it's at the end
  return trimEnd ? fixed.replace(/\.00$/, '') : fixed;
};

/**
 * Converts a string with underscores to camelCase
 * @param str A string with underscores/snakeCase
 * @returns A string with camelCase
 */
export const toCamel = (str: string) => str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());